/**
 * Phase 3 — Social outreach helpers.
 *
 * Builds NATIVE-APP deep links from a realtor's `*_url` field. Designed for
 * mobile-first use (the home inspector is out canvassing open houses on
 * their phone). On desktop the same handlers fall back to copying the web
 * URL to the clipboard so the realtor can be opened in a logged-in browser
 * session.
 *
 * Username/handle extraction is regex-based per platform — Realtor.ca-sourced
 * URLs are usually predictable canonical forms (no profile-ID gibberish).
 */

export const PLATFORMS = [
  { key: "fb", label: "Facebook", urlField: "facebook_url" },
  { key: "ig", label: "Instagram", urlField: "instagram_url" },
  { key: "li", label: "LinkedIn", urlField: "linkedin_url" },
  { key: "x", label: "X / Twitter", urlField: "twitter_url" },
  { key: "tt", label: "TikTok", urlField: "tiktok_url" },
  { key: "yt", label: "YouTube", urlField: "youtube_url" },
  { key: "th", label: "Threads", urlField: "threads_url" },
];

export const PLATFORM_BY_KEY = Object.fromEntries(PLATFORMS.map((p) => [p.key, p]));

/** True if we appear to be on a mobile device (iOS/Android UA sniff). */
export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /android|iphone|ipad|ipod|mobile/i.test(ua);
}

/**
 * Extract handle/profile-id from a stored `*_url`.
 * Returns the bare handle (no `@`, no `https://`). Returns "" if unrecognised.
 */
export function extractHandle(channel, rawUrl) {
  const url = (rawUrl || "").trim();
  if (!url) return "";
  // Most platforms: last non-empty path segment is the handle/id
  let path = "";
  try {
    path = new URL(url).pathname.replace(/^\/+|\/+$/g, "");
  } catch {
    // Not a URL — maybe already a bare handle
    path = url.replace(/^@/, "");
  }
  if (!path) return "";
  const segs = path.split("/").filter(Boolean);

  switch (channel) {
    case "fb": {
      // facebook.com/<vanity> or facebook.com/profile.php?id=123
      const m = url.match(/[?&]id=(\d+)/);
      if (m) return m[1]; // numeric profile id
      return segs[0] || "";
    }
    case "ig":
      return (segs[0] || "").replace(/^@/, "");
    case "li": {
      // linkedin.com/in/<vanity> | /pub/<old>/<id>/<id>/<id>
      const i = segs.indexOf("in");
      if (i >= 0 && segs[i + 1]) return segs[i + 1];
      return segs[segs.length - 1] || "";
    }
    case "x":
      return (segs[0] || "").replace(/^@/, "");
    case "tt":
      return (segs[0] || "").replace(/^@/, "");
    case "th":
      // threads.net/@handle or threads.com/@handle
      return (segs[0] || "").replace(/^@/, "");
    case "yt": {
      // youtube.com/channel/<id> | /@handle | /c/<vanity> | /user/<old>
      const ch = segs.indexOf("channel");
      if (ch >= 0 && segs[ch + 1]) return segs[ch + 1];
      const c = segs.indexOf("c");
      if (c >= 0 && segs[c + 1]) return segs[c + 1];
      const u = segs.indexOf("user");
      if (u >= 0 && segs[u + 1]) return segs[u + 1];
      return (segs[0] || "").replace(/^@/, "");
    }
    default:
      return segs[0] || "";
  }
}

/** Native-app deep link for a platform + handle. Returns null if unknown. */
export function nativeDeepLink(channel, handle) {
  if (!handle) return null;
  const h = encodeURIComponent(handle);
  switch (channel) {
    case "fb":
      // fb://profile/<numeric_or_vanity>
      return `fb://profile/${h}`;
    case "ig":
      return `instagram://user?username=${h}`;
    case "li":
      return `linkedin://in/${h}`;
    case "x":
      return `twitter://user?screen_name=${h}`;
    case "tt":
      // snssdk1233:// requires TikTok-numeric-id which we usually don't have;
      // fall back to web URL which the TikTok app handles on iOS/Android.
      return `https://www.tiktok.com/@${h}`;
    case "th":
      // barcelona:// is the Threads scheme (project codename); accepts handle.
      return `barcelona://user?username=${h}`;
    case "yt":
      // youtube:// works with channel/id or @handle
      return `youtube://channel/${h}`;
    default:
      return null;
  }
}

/** Web URL for a platform + handle. Used as desktop fallback or share target. */
export function webUrl(channel, handle, rawUrl) {
  // If we already have the original raw URL, prefer it (handles edge cases).
  if (rawUrl) return rawUrl;
  if (!handle) return "";
  switch (channel) {
    case "fb":
      return `https://www.facebook.com/${handle}`;
    case "ig":
      return `https://www.instagram.com/${handle}/`;
    case "li":
      return `https://www.linkedin.com/in/${handle}/`;
    case "x":
      return `https://x.com/${handle}`;
    case "tt":
      return `https://www.tiktok.com/@${handle}`;
    case "th":
      return `https://www.threads.net/@${handle}`;
    case "yt":
      return `https://www.youtube.com/${handle.startsWith("UC") ? `channel/${handle}` : `@${handle}`}`;
    default:
      return "";
  }
}

/**
 * Resolve the best link to open. On mobile we attempt the native deep link
 * AND set a delayed fallback to the web URL in case the app isn't installed
 * (the OS swallows the URI handler silently otherwise — see the well-known
 * "iframe + setTimeout" pattern).
 *
 * Returns { primary, fallback } strings. Caller decides whether to set
 * window.location.href = primary, or open the fallback in a new tab.
 */
export function buildOpenStrategy(channel, rawUrl) {
  const handle = extractHandle(channel, rawUrl);
  const native = nativeDeepLink(channel, handle);
  const web = webUrl(channel, handle, rawUrl);
  return { handle, native, web };
}

/** Robust clipboard helper. Returns true on success. */
export async function copyToClipboard(text) {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fall through to legacy path */
  }
  // Legacy fallback for older Safari / inside iframes without clipboard permission
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
